<template>
  <div class="player-wrapper" :class="{ 'controls-hidden': !started, 'icon-active': showIcon }">
    <vue-plyr ref="plyrRef" :options="plyrOptions">
      <video controls playsinline preload="none" :poster="post.cover || undefined">
        <source :src="post.preview" type="video/mp4" />
      </video>
    </vue-plyr>
    <div v-if="showIcon" class="play-icon play-icon--full" @click="playVideo">
      <font-awesome :icon="['fas', 'circle-play']" size="3x" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import VuePlyr from "vue-plyr";
import "vue-plyr/dist/vue-plyr.css";

export interface MakingOfPost {
  id: number;
  title: string;
  slug: string;
  imageAltText?: string | null;
  cover?: string | null;
  preview: string;
}

const props = defineProps<{
  post: MakingOfPost;
  active: boolean;
}>();

const plyrRef = ref<any>(null);
const showIcon = ref(true);
const started = ref(false);

const plyrOptions = ref({
  controls: [
    "rewind",
    "play",
    "fast-forward",
    "progress",
    "current-time",
    "duration",
    "mute",
    "volume",
    "captions",
    "fullscreen",
  ],
  fullscreen: {
    enabled: true,
    iosNative: true,
  },
  // ratio fixo: evita o colapso do player antes do metadata e mantém
  // todos os slides do carrossel com a mesma altura
  ratio: "16:9",
  debug: false,
});

function playVideo() {
  if (plyrRef.value?.player) {
    plyrRef.value.player.play();
  }
}

// slide saiu de foco (seta, swipe ou dot): pausa o vídeo em reprodução
watch(
  () => props.active,
  (isActive) => {
    if (!isActive) plyrRef.value?.player?.pause();
  }
);

watch(plyrRef, (value, old) => {
  if (old?.player) {
    old.player.off("play");
    old.player.off("pause");
    old.player.off("ended");
  }
  if (!value?.player) return;
  value.player.on("play", () => {
    showIcon.value = false;
    started.value = true;
  });
  value.player.on("pause", () => {
    showIcon.value = true;
  });
  value.player.on("ended", () => {
    showIcon.value = true;
  });
});

onBeforeUnmount(() => {
  if (plyrRef.value?.player) {
    plyrRef.value.player.off("play");
    plyrRef.value.player.off("pause");
    plyrRef.value.player.off("ended");
  }
});
</script>

<style scoped lang="css">
.player-wrapper {
  position: relative;
  /* o .carousel__slide é flex (justify-content: center); sem largura explícita
     o conteúdo encolhe para a largura intrínseca do vídeo */
  width: 100%;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ffffff;
  cursor: pointer;
  z-index: 1;
}

/* mobile: a área de toque cobre o vídeo para reproduzir ao tocar em qualquer parte */
@media screen and (max-width: 769px) {
  .play-icon--full {
    inset: 0;
    top: 0;
    left: 0;
    transform: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* com os controles visíveis (pausado/parado), libera a barra inferior do Plyr */
  .player-wrapper:not(.controls-hidden) .play-icon--full {
    bottom: 48px;
  }

}

/* antes do primeiro play: oculta a barra de controles do rodapé */
.player-wrapper.controls-hidden :deep(.plyr__controls) {
  display: none;
}

/* enquanto o ícone central está ativo (parado/pausado): escurece o vídeo */
.player-wrapper.icon-active :deep(.plyr__video-wrapper) {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.plyr video {
  width: 100%;
  object-fit: contain;
}

.plyr:fullscreen video {
  max-height: 100vh !important;
  width: 100vw !important;
  object-fit: contain;
}
</style>
